import React, { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useNavMode, useBrain } from "@/contexts";
import { useSidebar } from "@/contexts/ui/sidebar";
import { BulkUploadProvider } from "@/contexts";
import { GlobalRealtimeListeners } from "@/components/sync/AgentSubscriptionSyncListener";
import BulkUploadFloatingBadge from "@/components/knowledge-base/BulkUploadFloatingBadge";
import { ImpersonationBanner } from "./ImpersonationBanner";
import HelpGenieWidget from "@/components/brain/HelpGenieWidget";
import MobileNav from "./MobileNav";
import Sidebar from "./Sidebar";
import WorkspaceSwitcher from "./WorkspaceSwitcher";

const AppLayoutContent: React.FC = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { isOpen: helpGenieOpen, setIsOpen: setHelpGenieOpen, openBrain } = useBrain();
  const { sidebarOpen, setSidebarOpen, hasEverExpanded } = useSidebar();
  const openHelpGenie = useCallback(() => openBrain(), [openBrain]);
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  // Admin styling when in admin mode or admin paths
  const { mode } = useNavMode();
  const isAdminPage =
    mode === "admin" ||
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/application-management");

  useEffect(() => {
    const handleResize = () => {
      const mobileCheck = window.innerWidth < 768;
      if (mobileCheck !== isMobile) {
        setIsMobile(mobileCheck);
        if (mobileCheck) {
          setSidebarOpen(false);
        }
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile]);

  useEffect(() => {
    // Mobile UX: close sidebar only after an actual route change (not on mount / not on toggle)
    // This avoids interfering with the hamburger toggle and prevents "full reload" fallbacks.
    const prevPath = prevPathRef.current;
    prevPathRef.current = location.pathname;

    if (isMobile && sidebarOpen && prevPath !== location.pathname) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile, sidebarOpen, setSidebarOpen]);

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollContainerRef.current?.scrollTo(0, 0);
  }, [location.pathname]);

  const isMarketplace = location.pathname.includes('/admin/marketplace');
  useEffect(() => {
    if (!isMarketplace) return;
    const t = setTimeout(() => {
      requestAnimationFrame(() => {
        const el = scrollContainerRef.current;
        const parent = el?.parentElement;
        fetch('http://127.0.0.1:7242/ingest/518058b3-d4b4-4554-8ee1-afb67e98aaad', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'AppLayout.tsx:scroll-area', message: 'scroll container and parent dimensions', data: { scrollAreaOffset: el?.offsetHeight, scrollAreaScroll: el?.scrollHeight, parentOffset: parent?.offsetHeight, outerOffset: parent?.parentElement?.offsetHeight, bodyScroll: document.body.scrollHeight, innerHeight: window.innerHeight }, timestamp: Date.now(), sessionId: 'debug-session', hypothesisId: 'H2' }) }).catch(() => { });
      });
    }, 300);
    return () => clearTimeout(t);
  }, [isMarketplace, location.pathname]);

  return (
    <ImpersonationBanner>
    <div className={`flex flex-col h-screen-safe overflow-hidden ${isAdminPage ? "bg-[#D6409F]/5" : "bg-genie-background"}`}>
      <div className="flex flex-1 min-h-0">
        {isMobile && <MobileNav sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />}
        <Sidebar isMobile={isMobile} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} hasEverExpanded={hasEverExpanded} onOpenHelpGenie={openHelpGenie} />
        <div id="main-scroll-container" ref={scrollContainerRef} className={`flex-1 min-h-0 overflow-y-auto ${isMobile ? "pt-16" : ""}`}>
          <WorkspaceSwitcher />
          <main className={`min-h-full ${isAdminPage ? "bg-[#D6409F]/5" : ""}`}>
            <Outlet />
          </main>
        </div>
        <HelpGenieWidget open={helpGenieOpen} onOpenChange={setHelpGenieOpen} />
      </div>
    </div>
    </ImpersonationBanner>
  );
};

const AppLayout: React.FC = () => {
  return (
    <BulkUploadProvider>
      <GlobalRealtimeListeners />
      <AppLayoutContent />
      <BulkUploadFloatingBadge />
    </BulkUploadProvider>
  );
};

export default AppLayout;
